import { intlDateLocale, intlLocale, useTranslation } from "@linkhr/i18n";
import { formatMoney, isoToDate } from "@linkhr/i18n/format";

export interface LedgerFormat {
  locale: "en" | "ar";
  /** A name the server keeps per language. */
  name: (
    names: { readonly ar?: string | null; readonly en?: string | null } | null | undefined,
  ) => string;
  /** An amount at its currency's precision, negatives in parentheses, no currency sign. */
  money: (amount: string, currency: string) => string;
  /** A calendar date (`YYYY-MM-DD`). */
  day: (iso: string) => string;
  /** A calendar date's (or a `YYYY-MM` month's) month and year. */
  month: (iso: string) => string;
  /** An instant, on the company's clock (the reader's when none is given). */
  instant: (iso: string, timeZone?: string) => string;
  /** An exchange rate (a decimal string, up to 12 places) without trailing zeros. */
  rate: (value: string) => string;
  /** A count, in the numerals preference. */
  number: (value: number) => string;
}

/** The ledger screens' formatters, in the UI language and the numerals preference. */
export function useLedgerFormat(): LedgerFormat {
  const { i18n } = useTranslation("accounting");
  const locale: "en" | "ar" = i18n.language.startsWith("ar") ? "ar" : "en";
  const dateLocale = intlDateLocale(locale);
  const dateText = (iso: string, options: Intl.DateTimeFormatOptions): string =>
    isoToDate(iso)?.toLocaleDateString(dateLocale, options) ?? iso;
  return {
    locale,
    // An empty name in the UI language falls back to the other one.
    name: (names) => {
      const own = names?.[locale];
      return own != null && own !== "" ? own : names?.en || names?.ar || "";
    },
    money: (amount, currency) =>
      formatMoney(amount, { currency, locale, style: "full", sign: "accounting", display: "none" }),
    day: (iso) => dateText(iso, { day: "numeric", month: "short", year: "numeric" }),
    month: (iso) =>
      dateText(iso.length === 7 ? `${iso}-01` : iso, { month: "long", year: "numeric" }),
    instant: (iso, timeZone) =>
      new Date(iso).toLocaleString(dateLocale, {
        timeZone,
        dateStyle: "medium",
        timeStyle: "short",
      }),
    rate: (value) =>
      // Intl formats a decimal string exactly at runtime; ES2022 lib types it number-only.
      new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: 12 }).format(
        value as unknown as number,
      ),
    number: (value) => new Intl.NumberFormat(intlLocale(locale)).format(value),
  };
}

/** Keeps a name or a number in its own direction inside a sentence in the other (nothing stays nothing). */
export const isolate = (text: string): string => (text !== "" ? `\u2068${text}\u2069` : "");
